import type { FC, PropsWithChildren } from 'react';
import { createContext, useContext, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SlideOut } from '@interstate/components/SlideOut';
import { Typography } from '@interstate/components/Typography';
import {
    altDealStructureCancelClicked,
    altDealStructureCreateScenarioClicked,
    altDealStructureLinkClicked
} from '@makemydeal/dr-dash-store/actions/deskingActionCreators';
import { ensureAltStructureLeaseGrid } from '@makemydeal/dr-dash-store/actions/offerRedux';
import { shouldFetchAltDealStructures } from '@makemydeal/dr-dash-store/selectors/altDealStructureSelectors';
import * as vehicleSelectors from '@makemydeal/dr-dash-store/selectors/vehicleSelectors';
import { AiAltDealStructureSlideoutContent } from './AiAltDealStructureSlideoutContent';
import { SlideoutFooter } from './SlideoutFooter';
import { AiLogoIcon } from '@makemydeal/dr-shared-components';
import { useGridData, useGridActions, useAiAltDealStructureOpenState } from './contexts';
import { buildDealStructurePredictionPayload, buildDealStructurePredictionsFromSlots } from './altDealStructureCdlUtils';

interface AiAltDealStructureContextValue {
    showAltDealStructures: () => void;
    hide: () => void;
}

const AiAltDealStructureContext = createContext<AiAltDealStructureContextValue | undefined>(undefined);

export const AiAltDealStructureProvider: FC<PropsWithChildren> = ({ children }) => {
    const dispatch = useDispatch();
    const vehicleAnalyticsPayload = useSelector(vehicleSelectors.getVehicleAnalyticsPayload);
    const { slots } = useGridData();
    const { canCreateScenarios, handleCreateScenarios, resetSelection, selectedCells } = useGridActions();
    // Open state is owned by the shared OpenStateContext (above the grid container) so the grid can gate
    // its AI fetch on slideout open instead of fetching eagerly at mount.
    const { isOpen, open, close } = useAiAltDealStructureOpenState();
    const shouldFetch = useSelector(shouldFetchAltDealStructures);

    const showAltDealStructures = useCallback(() => {
        dispatch(altDealStructureLinkClicked(vehicleAnalyticsPayload)); // dc-mv-288
        resetSelection();
        open();
        // A deal/vehicle change always happens with this slideout closed, so the refresh runs here on the
        // next open: force a fresh lease re-PQ when the grid is stale, otherwise ensure-once reuses cache.
        dispatch(ensureAltStructureLeaseGrid(shouldFetch));
    }, [dispatch, vehicleAnalyticsPayload, resetSelection, open, shouldFetch]);

    const hide = close;

    const handleCancel = useCallback(() => {
        // dc-mv-291: all presented (ready) structures.
        dispatch(altDealStructureCancelClicked(vehicleAnalyticsPayload, buildDealStructurePredictionsFromSlots(slots)));
        hide();
    }, [dispatch, hide, slots, vehicleAnalyticsPayload]);

    const handleCreateScenario = useCallback(() => {
        // dc-mv-290: only the selected structures (finance and/or lease).
        const dealStructure = [selectedCells.finance, selectedCells.lease]
            .filter((cell): cell is NonNullable<typeof cell> => cell != null)
            .map(buildDealStructurePredictionPayload);
        dispatch(
            altDealStructureCreateScenarioClicked({
                vehicleInfo: vehicleAnalyticsPayload,
                dealStructure
            })
        );
        handleCreateScenarios();
        hide();
    }, [dispatch, selectedCells, vehicleAnalyticsPayload, handleCreateScenarios, hide]);

    return (
        <AiAltDealStructureContext.Provider value={{ showAltDealStructures, hide }}>
            {children}
            <SlideOut
                header={
                    <Typography variant="h3" sx={{ padding: '0 24px', gap: '8px', display: 'flex', alignItems: 'center' }}>
                        Alternative Deal Structure Options
                        <AiLogoIcon data-testid="ai-alt-deal-structure-header-ai-logo" fontSize="24px" ariaHidden={true} />
                    </Typography>
                }
                show={isOpen}
                position="right"
                panelWidth="auto"
                data-testid="ai-alt-deal-structure-slideout"
                onHide={handleCancel}
                footer={{
                    footerComponent: (
                        <SlideoutFooter
                            onCancel={handleCancel}
                            onCreateScenarios={handleCreateScenario}
                            canCreateScenarios={canCreateScenarios}
                        />
                    )
                }}
                sx={{
                    '.MuiPaper-root': {
                        boxShadow: 'unset'
                    },
                    '.MuiPaper-root>div': {
                        padding: '0'
                    },
                    '.MuiPaper-root.MuiDrawer-paper': {
                        padding: '24px 0 0 0',
                        display: 'flex',
                        flexDirection: 'column'
                    },
                    '.ids-SlideOut-content': {
                        flex: '1 1 auto',
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        padding: '0 24px 24px !important'
                    },
                    '.MuiDialogActions-root': {
                        padding: '16px 24px',
                        backgroundColor: '#f5f5f5',
                        flex: '0 0 auto'
                    },
                    '.MuiDrawer-paper .MuiPaper-root': {
                        position: 'unset'
                    },
                    '.ids-SlideOut-overflow-footer > div': {
                        backgroundColor: 'rgba(244, 244, 244, 0.96)',
                        borderTop: '1px solid #B2B2B2'
                    },
                    '.MuiPaper-root.MuiDrawer-paper>.interstate-sliding-drawer-dialog-title-container': {
                        padding: '.5rem'
                    }
                }}
            >
                <AiAltDealStructureSlideoutContent />
            </SlideOut>
        </AiAltDealStructureContext.Provider>
    );
};

export const useAiAltDealStructureSlideout = () => {
    const context = useContext(AiAltDealStructureContext);
    if (!context) {
        throw new Error('useAiAltDealStructureSlideout must be used within an AiAltDealStructureProvider');
    }
    return context;
};
